import {
  createContext,
  Suspense,
  useContext,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Outlet, useLocation } from 'react-router';
import type { Dictionary } from '../content/pt-BR';
import { useTip } from './Tip';
import { useT } from '../lib/i18n';
import { Icon, type IconName } from './Icon';
import { ZendaWordmark } from './ZendaWordmark';
import { useAuth } from '../lib/auth';
import { useConversations } from '../lib/queries';
import { useStream } from '../lib/stream';
import { PlayerBar } from './Player';
import { initials } from '../lib/format';
import { CustoDoMes } from './CustoDoMes';
import { PilotoNaBarra } from './PilotoNaBarra';
import { BotaoDeAjuda, chaveDaRota, ProvedorDeAjuda, type ChaveDeAjuda } from './AjudaDaTela';

interface NavItem {
  readonly to: string;
  readonly icon: IconName;
  readonly label: string;
  readonly title: string;
  readonly subtitle: string;
}

/**
 * O canto direito da topbar, emprestado a pagina aberta.
 *
 * Portal, e nao prop: o roteador entrega a pagina por <Outlet/>, e nao ha por
 * onde uma prop passar. O contexto guarda o no via ref-callback porque o portal
 * precisa RE-RENDERIZAR quando ele existe — com useRef nada avisaria.
 */
const TopbarSlot = createContext<HTMLElement | null>(null);

export function TopbarActions({ children }: { readonly children: ReactNode }): ReactElement | null {
  const slot = useContext(TopbarSlot);
  return slot === null ? null : createPortal(children, slot);
}

/**
 * A navegação e os títulos vêm do dicionário — a mesma lista serve aos dois, e
 * separá-los deixaria o título de uma página divergir do nome dela no menu.
 */
function navItems(t: Dictionary): readonly NavItem[] {
  return [
    { to: '/', icon: 'grid', label: t.nav.dashboard, ...t.pages.dashboard },
    { to: '/inbox', icon: 'chat', label: t.nav.inbox, ...t.pages.inbox },
    { to: '/clientes', icon: 'user', label: t.nav.clients, ...t.pages.clients },
    { to: '/agenda', icon: 'calendar', label: t.nav.agenda, ...t.pages.agenda },
    { to: '/equipe', icon: 'team', label: t.nav.team, ...t.pages.team },
    { to: '/configuracoes', icon: 'settings', label: t.nav.settings, ...t.pages.settings },
  ];
}

/**
 * Páginas que existem mas não aparecem no menu.
 *
 * Sem isto, uma rota fora da navegação herdaria o título "Painel" — e numa tela
 * gravada para o App Review o revisor leria "Painel" enquanto assiste à criação
 * de um template.
 */
function hiddenItems(t: Dictionary): readonly NavItem[] {
  return [
    {
      to: '/verificacao',
      icon: 'signal',
      label: t.nav.verification,
      ...t.pages.verification,
    },
  ];
}

function currentNav(t: Dictionary, pathname: string): NavItem {
  const all = [...navItems(t), ...hiddenItems(t)];
  const match = all.find((n) => n.to !== '/' && pathname.startsWith(n.to));
  return match ?? navItems(t)[0]!;
}

/**
 * A barra lateral recolhida vale a largura que devolve: sao 236 px que vao para
 * a conversa, que e onde a atendente passa o dia. A preferencia mora no
 * navegador — e jeito de trabalhar, nao dado — pelo mesmo motivo da ficha do
 * contato no inbox.
 */
const SIDEBAR_KEY = 'zenda.sidebar';

function lerRecolhida(): boolean {
  return window.localStorage.getItem(SIDEBAR_KEY) === 'recolhida';
}

export function AppShell(): ReactElement {
  const t = useT();
  const tip = useTip();
  const [recolhida, setRecolhida] = useState<boolean>(lerRecolhida);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [ajudaDaPagina, setAjudaDaPagina] = useState<ChaveDeAjuda | null>(null);
  const { me, signOut, recarregarMe } = useAuth();
  const location = useLocation();
  const conversations = useConversations();
  const page = currentNav(t, location.pathname);
  const ajuda = ajudaDaPagina ?? chaveDaRota(location.pathname);
  const nav = navItems(t);

  // O stream vive no SHELL, e nao numa pagina: a atendente pode estar na agenda
  // quando a mensagem chega, e o contador do menu precisa mudar do mesmo jeito.
  useStream(me !== null, recarregarMe, me?.tenantId);

  const unread = (conversations.data ?? []).reduce((sum, c) => sum + c.unreadCount, 0);

  function alternarBarra(): void {
    setRecolhida((atual) => {
      window.localStorage.setItem(SIDEBAR_KEY, atual ? 'aberta' : 'recolhida');
      return !atual;
    });
  }

  return (
    <div className={`shell${recolhida ? ' is-collapsed' : ''}`}>
      <aside className="sidebar">
        <div className="brand">
          {/* A marca ja contem a palavra — o texto ao lado seria o nome duas vezes. */}
          <ZendaWordmark className="brand__logo" mark={recolhida} />
          <span className="sr-only">Zenda</span>
          {/* Mesmo icone que abre a ficha do paciente na conversa: em ambos os
              casos a acao e a mesma — mostrar ou esconder um painel lateral. */}
          <button
            type="button"
            className="brand__toggle"
            onClick={alternarBarra}
            aria-expanded={!recolhida}
            aria-label={recolhida ? t.nav.expandMenu : t.nav.collapseMenu}
            {...tip(recolhida ? t.nav.expandMenu : t.nav.collapseMenu)}
          >
            <Icon name="panel" size={17} />
          </button>
        </div>

        <nav className="nav" aria-label={t.nav.sections}>
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }: { isActive: boolean }) =>
                `nav__item${isActive ? ' is-active' : ''}`
              }
            >
              <span className="nav__icon" {...(recolhida ? tip(item.label) : {})}>
                <Icon name={item.icon} size={18} />
              </span>
              <span className="nav__label">{item.label}</span>
              {item.to === '/inbox' && unread > 0 ? (
                <span className="nav__count num">{unread}</span>
              ) : null}
              {item.to === '/equipe' && (me?.pendingInvites ?? 0) > 0 ? (
                <span className="nav__count num">{me?.pendingInvites}</span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar__spacer" />

        <PlayerBar />

        <div className="who">
          <div className="who__id">
            <span className="who__avatar">
              {initials(me?.displayName ?? me?.fullName ?? 'Zenda')}
            </span>
            <div className="who__meta">
              <p className="who__name">{me?.displayName ?? me?.fullName ?? '—'}</p>
              <p className="who__org">{me?.tenantName ?? '—'}</p>
            </div>
            <button
              type="button"
              className="who__out"
              onClick={signOut}
              aria-label={t.nav.signOut}
              {...tip(t.nav.signOut)}
            >
              <Icon name="logout" size={17} />
            </button>
          </div>
          <PilotoNaBarra recolhida={recolhida} />
          {recolhida ? null : <CustoDoMes ativo={me !== null} />}
        </div>
      </aside>

      <div className="shell__main">
        <header className="topbar">
          <div>
            <div className="topbar__linha">
              <h1 className="topbar__title">{page.title}</h1>
              {ajuda === null ? null : <BotaoDeAjuda key={ajuda} chave={ajuda} />}
            </div>
            <p className="topbar__subtitle">{page.subtitle}</p>
          </div>
          <div className="topbar__actions" ref={setSlot} />
        </header>

        {(me?.pendingInvites ?? 0) > 0 && !location.pathname.startsWith('/equipe') ? (
          <NavLink to="/equipe" className="shell__convite">
            <Icon name="team" size={16} />
            <span>{t.equipe.faixaConvite(me?.pendingInvites ?? 0)}</span>
            <span className="shell__convite-ir">{t.equipe.verEAceitar}</span>
          </NavLink>
        ) : null}

        <main className="content">
          <TopbarSlot.Provider value={slot}>
            <ProvedorDeAjuda value={setAjudaDaPagina}>
              <Suspense fallback={null}>
                <Outlet />
              </Suspense>
            </ProvedorDeAjuda>
          </TopbarSlot.Provider>
        </main>
      </div>
    </div>
  );
}
